import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { Loader } from '@/components/Loader/Loader'
import { useAppContext } from '@/hooks'
import { MRDiffDrawer } from '@/pages/User/pages/MergeRequests/components/MRDiffDrawer'
import { changeToMrStructure } from '@/pages/User/pages/MergeRequests/mapMergeRequest'
import { MrStructure } from '@/pages/User/pages/MergeRequests/types'
import { useRebaseBranchMutation } from '@/rtkApi'
import {
	BranchRebaseConflictDto,
	BranchRebaseConflictResolutions,
	BranchRebaseResultDtoRead,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import {
	getApiErrorDescription,
	logErrorAndNotifyWithToast,
} from '@/utils/errorHandling'

import {
	rebaseFieldForPath,
	rebaseResolutionValue,
	rebaseResultKind,
	rebaseSelectionsComplete,
	RebaseChoice,
} from '../rebaseBranch'
import { BranchRecord } from '../types'

type RebaseBranchDialogProps = {
	branch: BranchRecord | null
	isOpen: boolean
	onClose: () => void
}

const toStructure = (group: BranchRebaseConflictDto): MrStructure =>
	changeToMrStructure({
		changeType: 'MODIFIED',
		code: group.code,
		conflicts: group.conflicts,
		name: group.name,
		structureId: group.structureId,
	})

export const RebaseBranchDialog = ({
	branch,
	isOpen,
	onClose,
}: RebaseBranchDialogProps) => {
	const { t } = useAppContext()
	const [rebaseBranch] = useRebaseBranchMutation()
	const [loading, setLoading] = useState(false)
	const [result, setResult] = useState<BranchRebaseResultDtoRead | null>(null)
	const [structures, setStructures] = useState<MrStructure[]>([])
	const [selected, setSelected] = useState<BranchRebaseConflictResolutions>({})
	const [openStructureId, setOpenStructureId] = useState<string | null>(null)
	const started = useRef(false)
	const openRef = useRef(isOpen)
	openRef.current = isOpen

	const conflicts = result?.conflicts ?? []
	const kind = result ? rebaseResultKind(result) : null
	const openStructure =
		structures.find((structure) => structure.key === openStructureId) ?? null

	const applyResult = useCallback(
		(next: BranchRebaseResultDtoRead) => {
			setResult(next)
			if (rebaseResultKind(next) === 'conflicts') {
				setStructures((next.conflicts ?? []).map(toStructure))
				setSelected({})
				return
			}
			setStructures([])
			setSelected({})
			if (rebaseResultKind(next) === 'done') {
				toast(t('BRANCHES_REBASE_SUCCESS'), { type: 'success' })
				onClose()
			}
		},
		[onClose, t],
	)

	useEffect(() => {
		if (!isOpen || !branch) {
			started.current = false
			setResult(null)
			setStructures([])
			setSelected({})
			setOpenStructureId(null)
			return
		}
		if (started.current) return
		started.current = true
		setLoading(true)
		void rebaseBranch({
			branchId: Number(branch.id),
			branchRebaseRequestDto: {},
		})
			.unwrap()
			.then((next) => {
				if (openRef.current) applyResult(next)
			})
			.catch((error: unknown) => {
				if (!openRef.current) return
				logErrorAndNotifyWithToast({
					context: { operation: 'RebaseBranchDialog.start' },
					detail: getApiErrorDescription(error),
					error,
					messageKey: 'BRANCHES_REBASE_FAILED',
					t,
				})
				onClose()
			})
			.finally(() => setLoading(false))
	}, [applyResult, branch, isOpen, onClose, rebaseBranch, t])

	const recordResolution = async (
		structure: MrStructure,
		enginePath: string,
		choice: RebaseChoice,
		customValue?: unknown,
	) => {
		const group = conflicts.find(
			(item) => String(item.structureId) === structure.key,
		)
		const field = group ? rebaseFieldForPath(group, enginePath) : undefined
		if (!group?.structureId || !field?.fieldPath) return
		const value = rebaseResolutionValue(field, choice, customValue)
		setSelected((current) => ({
			...current,
			[String(group.structureId)]: {
				...current[String(group.structureId)],
				[field.fieldPath as string]: value,
			},
		}))
	}

	const apply = async () => {
		if (!branch) return
		setLoading(true)
		try {
			const next = await rebaseBranch({
				branchId: Number(branch.id),
				branchRebaseRequestDto: { conflictResolutions: selected },
			}).unwrap()
			applyResult(next)
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'RebaseBranchDialog.apply' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'BRANCHES_REBASE_FAILED',
				t,
			})
		} finally {
			setLoading(false)
		}
	}

	return (
		<>
			<Dialog
				open={isOpen && !openStructure}
				onClose={onClose}
				maxWidth="sm"
				fullWidth
				slotProps={{ paper: { sx: { borderRadius: '14px' } } }}
			>
				<DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: '#1F4954' }}>
					{t('BRANCHES_REBASE')}
				</DialogTitle>
				<DialogContent>
					{loading ? <Loader loaded={false} /> : null}
					{kind === 'blocked' ? (
						<Box>
							<Typography sx={{ fontSize: 14, mb: 1.5 }}>
								{t('BRANCHES_REBASE_BLOCKED')}
							</Typography>
							{(result?.blockingMergeRequests ?? []).map((request) => (
								<Typography key={request.id} sx={{ fontSize: 13, color: '#3D3D3D' }}>
									{request.title || String(request.id ?? '')}
								</Typography>
							))}
						</Box>
					) : null}
					{kind === 'message' ? (
						<Typography sx={{ fontSize: 14 }}>{result?.message}</Typography>
					) : null}
					{kind === 'conflicts' ? (
						<Box sx={{ display: 'grid', gap: 1 }}>
							<Typography sx={{ fontSize: 14 }}>
								{t('BRANCHES_REBASE_CONFLICTS')}
							</Typography>
							{conflicts.map((group) => {
								const resolved = rebaseSelectionsComplete([group], selected)
								const label = group.name || group.code || String(group.structureId ?? '')
								return (
									<Box
										key={group.structureId}
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											gap: 1,
										}}
									>
										<Typography sx={{ fontSize: 13 }}>{label}</Typography>
										<Button
											size="small"
											onClick={() =>
												setOpenStructureId(String(group.structureId ?? ''))
											}
											sx={{ textTransform: 'none' }}
										>
											{resolved
												? t('BRANCHES_REBASE_RESOLVED')
												: t('BRANCHES_REBASE_RESOLVE')}
										</Button>
									</Box>
								)
							})}
						</Box>
					) : null}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5 }}>
					{kind === 'conflicts' ? (
						<Button
							variant="contained"
							disableElevation
							disabled={loading || !rebaseSelectionsComplete(conflicts, selected)}
							onClick={() => void apply()}
							sx={{ textTransform: 'none', borderRadius: '8px' }}
						>
							{t('BRANCHES_REBASE_APPLY')}
						</Button>
					) : null}
					<Button
						onClick={onClose}
						sx={{ textTransform: 'none', borderRadius: '8px' }}
					>
						{kind === 'conflicts' ? t('CANCEL') : t('CLOSE')}
					</Button>
				</DialogActions>
			</Dialog>
			<MRDiffDrawer
				open={Boolean(openStructure)}
				structure={openStructure}
				sourceBranchName={branch?.name}
				targetBranchName={branch?.sourceBranchName ?? undefined}
				onClose={() => setOpenStructureId(null)}
				onResolve={async (structure, node, resolution, customValue) => {
					await recordResolution(
						structure,
						node.path,
						resolution,
						customValue,
					)
				}}
			/>
		</>
	)
}
